import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation, useHistory } from 'react-router-dom';
import Menuicon from '../assets/img/menu-icon.svg';
import Mypoints from '../assets/img/mypoints.svg';
import Gamemenu from '../assets/img/gme-menu.svg';
import Profileicn from '../assets/img/profile-icn.svg';
import Shopicn from '../assets/img/shop.svg';
import Scoreboard from '../assets/img/score-board.svg';
import Chat from '../assets/img/chat.svg';
import Logoff from '../assets/img/log-off.svg';

import AdMessage from './Admessage';
import { profileActions } from '../actions';
import { userActions } from '../actions';
import { getSocket } from '../services/socket';
import { prefetchService } from '../services';
import { useLoggedUser } from '../hooks/useLoggedUser';
import { hasValidSession } from '../helpers/authUtils';

const Navbar = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    const [isShopOpen, setIsShopOpen] = useState(false);
    const getActiveClass = (path) => (location.pathname === path ? 'active' : '');
    const [displayPoints, setDisplayPoints] = useState({
        TotalScore: 0,
        AwardCoins: 0
    });
    // Increment when app recreates socket after tab visibility (so we re-join room on new socket)
    const [socketReconnectKey, setSocketReconnectKey] = useState(0);

    const loggedUser = useLoggedUser();
    const studentProfileId = loggedUser?.data?.StudentProfileID;

    const { mypoints, showAwardModal, awardMessage } = useSelector((state) => state.mypoints);

    useEffect(() => {
        if (!studentProfileId) {
            setDisplayPoints({ TotalScore: 0, AwardCoins: 0 });
            return;
        }

        if (mypoints && typeof mypoints === 'object') {
            setDisplayPoints({
                TotalScore: mypoints.TotalScore ?? 0,
                AwardCoins: mypoints.AwardCoins ?? 0
            });
        }
    }, [studentProfileId, mypoints]);

    useEffect(() => {
        const handler = () => setSocketReconnectKey((k) => k + 1);
        document.addEventListener('socketReconnected', handler);
        return () => document.removeEventListener('socketReconnected', handler);
    }, []);

    useEffect(() => {
        if (!studentProfileId || !hasValidSession()) return;

        dispatch(profileActions.fetchMypoints(studentProfileId));

        const socket = getSocket();
        if (!socket) return;

        const handleConnect = () => {
            // Keep room join on Navbar reconnect path as well (App also joins).
            socket.emit('joinStudentRoom', {
                StudentProfileID: studentProfileId,
            });
        };

        const handleAwardCoins = (data) => {
            if (!data) return;

            dispatch(
                profileActions.updateMypointsFromSocket({
                    TotalScore: data.TotalScore,
                    AwardCoins: data.AwardCoins,
                    IsAwarded: data.IsAwarded,
                    AwardFlagMessages: data.AwardFlagMessages,
                })
            );
        };

        // JOIN ROOM IMMEDIATELY IF ALREADY CONNECTED
        if (socket.connected) {
            handleConnect();
        }

        // RE-JOIN ON RECONNECT
        socket.on('connect', handleConnect);
        socket.on('awardCoins', handleAwardCoins);

        return () => {
            socket.off('connect', handleConnect);
            socket.off('awardCoins', handleAwardCoins);
        };
    }, [studentProfileId, dispatch, socketReconnectKey]);

    useEffect(() => {
        if (showAwardModal) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }
        return () => document.body.classList.remove('modal-open');
    }, [showAwardModal]);

    const closeAwardModal = () => {
        dispatch(profileActions.hideAwardPopup());
        dispatch(profileActions.resetAwardFlags());
    };

    const toggleSubmenu = () => {
        setIsShopOpen((prev) => !prev);
    };

    const prefetchRoute = useCallback((pathname) => {
        if (!hasValidSession()) return;
        prefetchService.prefetchPageData(pathname, loggedUser);
    }, [loggedUser]);

    const getPrefetchHandlers = useCallback((pathname) => ({
        onMouseEnter: () => prefetchRoute(pathname),
        onFocus: () => prefetchRoute(pathname),
        onTouchStart: () => prefetchRoute(pathname),
    }), [prefetchRoute]);

    const handleLogout = () => {
        dispatch(userActions.logout());
        history.push('/login');
    };

    return (
        <div>
            <nav className="navbar navbar-expand-sm">
                <div className="container-fluid">
                    <a className="navbar-brand d-flex align-items-left" data-bs-toggle="dropdown" data-bs-auto-close="false">
                        <div>
                            <img className="menu-icn" src={Menuicon} alt="Menu" />
                        </div>
                    </a>
                    <ul className="nav-menu-drp dropdown-menu">
                        <li>
                            <Link to="/mypoints" className={`dropdown-item ${getActiveClass('/mypoints')}`} {...getPrefetchHandlers('/mypoints')}>
                                <img className="w-15" src={Mypoints} alt="My Points" /> My Points
                            </Link>
                        </li>
                        <li>
                            <Link to="/" className={`dropdown-item ${getActiveClass('/')}`} {...getPrefetchHandlers('/')}>
                                <img className="w-15" src={Gamemenu} alt="Game Menu" /> Game Menu
                            </Link>
                        </li>
                        <li>
                            <Link to="/scoreboard" className={`dropdown-item ${getActiveClass('/scoreboard')}`} {...getPrefetchHandlers('/scoreboard')}>
                                <img className="w-15" src={Scoreboard} alt="Scoreboard" /> Score Board
                            </Link>
                        </li>
                        <li>
                            <Link to="/myprofile" className={`dropdown-item ${getActiveClass('/myprofile')}`} {...getPrefetchHandlers('/myprofile')}>
                                <img className="w-15" src={Profileicn} alt="Profile" /> Profile
                            </Link>
                        </li>
                        <li className="shop-li" onClick={toggleSubmenu}>
                            <img className="w-15" src={Shopicn} alt="Shop" /> Shop
                            <ul className={`submenu ${isShopOpen ? "open" : ""}`}>
                                <li><Link to="/purchasegame" className={`dropdown-item ${getActiveClass('/purchasegame')}`} {...getPrefetchHandlers('/purchasegame')}>Game</Link></li>
                                <li><Link to="/purchaseavatar" className={`dropdown-item ${getActiveClass('/purchaseavatar')}`} {...getPrefetchHandlers('/purchaseavatar')}>Profile Avatar</Link></li>
                            </ul>
                        </li>
                        <li>
                            <Link to="/chat" className={`dropdown-item ${getActiveClass('/chat')}`} {...getPrefetchHandlers('/chat')}>
                                <img className="w-15" src={Chat} alt="Chat" /> Chat
                            </Link>
                        </li>
                        <li>
                            <button
                                type="button"
                                className="dropdown-item"
                                onClick={handleLogout}
                                style={{ background: 'none', border: 'none' }}
                            >
                                <img className="w-15" src={Logoff} alt="Logoff" /> Logoff
                            </button>
                        </li>
                    </ul>

                    <div className="stats-box bg-white p-1 text-start ms-auto">
                        <p className="mb-0">Score: <span className="b-font">{displayPoints.TotalScore}</span></p>
                        {/* <p className="mb-0">Coins: <span className="b-font">12345</span></p> */}
                        <p className="mb-0">Coins: <span className="b-font">{displayPoints.AwardCoins}</span></p>
                    </div>
                </div>
            </nav>
            <AdMessage />

            {/* Award Modal */}
            {showAwardModal && (
                <div
                    className="modal alrt-mdl"
                    id="alert-modal"
                    style={{ display: 'block' }}
                >
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-body ad-modal pt-5">
                                <div className="w-100 d-flex align-items-center justify-content-center">
                                    <label className="b-font alt-label">{awardMessage}</label>
                                </div>
                                <div className="w-100 mt-4 mb-4 d-flex align-items-center justify-content-center">
                                    <button
                                        className="accept-btn mt-2"
                                        onClick={() => closeAwardModal()}
                                    >
                                        OK
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Navbar;
